import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

/**
 * Shared pickup-time formatting for the web app.
 *
 * Reservations store an exact instant (`pickupDatetime`, ISO/UTC) plus the IANA
 * zone of the pickup location (`pickupTimezone`). Every surface that shows a
 * pickup time must render the instant **in the pickup zone** — never via bare
 * `new Date(iso)` + `format()`, which silently uses the viewer's browser zone
 * (an LA viewer of a 3 PM New York pickup would see 12:00 PM).
 */

/** An instant, either as an ISO/UTC string (API JSON) or a Date. */
export type Instant = string | Date;

/**
 * Format a pickup instant in its own timezone for display.
 * @param iso - The pickup datetime (ISO/UTC string or Date).
 * @param timezone - The IANA timezone to render it in.
 * @param opts - Intl date/time formatting options (supports `timeZoneName`).
 * @returns A localized string (falls back to the raw ISO on a bad timezone).
 */
export function formatInZone(
  iso: Instant,
  timezone: string,
  opts: Intl.DateTimeFormatOptions
): string {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: timezone, ...opts }).format(new Date(iso));
  } catch {
    return typeof iso === "string" ? iso : iso.toISOString();
  }
}

/**
 * Format the calendar date of an instant in its zone, e.g. "Wednesday, July 1, 2026".
 * @param iso - The datetime (ISO/UTC string or Date).
 * @param timezone - The IANA timezone to render it in.
 * @returns The long-form date string.
 */
export function formatDateInZone(iso: Instant, timezone: string): string {
  return formatInZone(iso, timezone, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format the clock time of an instant in its zone, e.g. "3:00 PM EDT".
 * @param iso - The datetime (ISO/UTC string or Date).
 * @param timezone - The IANA timezone to render it in.
 * @param options - Display options.
 * @param options.withZone - Set false to omit the zone abbreviation.
 * @returns The time string, with the zone abbreviation by default.
 */
export function formatTimeInZone(
  iso: Instant,
  timezone: string,
  options: { withZone?: boolean } = {}
): string {
  const { withZone = true } = options;
  return formatInZone(iso, timezone, {
    hour: "numeric",
    minute: "2-digit",
    ...(withZone ? { timeZoneName: "short" as const } : {}),
  });
}

/**
 * The short, DST-aware abbreviation of a zone at a given instant (e.g. "EDT").
 * @param timezone - The IANA timezone.
 * @param at - The instant to evaluate DST at (defaults to now).
 * @returns The abbreviation, or "" if the zone is invalid.
 */
export function zoneAbbreviation(timezone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(at);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/**
 * A human-friendly city label derived from an IANA zone id, e.g.
 * `America/New_York` → "New York", `America/Argentina/Buenos_Aires` →
 * "Buenos Aires". Used for customer-facing copy ("All times are New York
 * time"); never for any time math (the IANA id drives conversion).
 * @param timezone - The IANA timezone id.
 * @returns The last segment with underscores replaced by spaces (raw input when empty).
 */
export function zoneCityLabel(timezone: string): string {
  if (!timezone) return "";
  const last = timezone.split("/").pop() ?? timezone;
  return last.replace(/_/g, " ");
}

/**
 * The viewer's resolved IANA timezone (browser/runtime zone). Used only to
 * decide whether to show the timezone caveat — never to interpret or store a
 * pickup time.
 * @returns The resolved IANA zone (e.g. "America/Los_Angeles").
 */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Whether two IANA zone ids are the same zone. Keyed off the id, not the UTC
 * offset, so distinct zones that momentarily share an offset (e.g. Phoenix vs
 * Denver in summer) are still treated as different.
 * @param a - The first zone id (or undefined).
 * @param b - The second zone id (or undefined).
 * @returns True only when both are present and identical.
 */
export function sameZone(a: string | undefined, b: string | undefined): boolean {
  return !!a && !!b && a === b;
}

/**
 * Zero-pad a date part to two digits.
 * @param n - The date part.
 * @returns The two-digit string.
 */
const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Convert a stored ISO datetime to the `YYYY-MM-DDTHH:mm` value a
 * `datetime-local` input expects — the wall-clock time in `timezone` when
 * given, otherwise in the browser's local zone. Never use
 * `toISOString().slice(...)` for this: that yields UTC wall time.
 * @param iso - The stored ISO datetime string (or undefined).
 * @param timezone - Optional IANA zone to render the wall time in.
 * @returns The datetime-local input value ("" for empty/invalid input).
 */
export function isoToDatetimeLocal(iso: string | undefined, timezone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (timezone) {
    try {
      return formatInTimeZone(d, timezone, "yyyy-MM-dd'T'HH:mm");
    } catch {
      return "";
    }
  }
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert a `datetime-local` input value to an ISO instant, interpreting the
 * wall-clock time in `timezone` when given, otherwise in the browser's zone.
 * @param value - The `YYYY-MM-DDTHH:mm` input value.
 * @param timezone - Optional IANA zone the wall time is meant in.
 * @returns The ISO string ("" for empty/invalid input).
 */
export function datetimeLocalToIso(value: string, timezone?: string): string {
  if (!value) return "";
  try {
    const d = timezone ? fromZonedTime(value, timezone) : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString();
  } catch {
    return "";
  }
}

/**
 * The current wall-clock time as a `datetime-local` value, for use as a
 * picker's `min` attribute.
 * @param timezone - Optional IANA zone (defaults to the browser's zone).
 * @returns The `YYYY-MM-DDTHH:mm` string for "now".
 */
export function datetimeLocalNow(timezone?: string): string {
  return isoToDatetimeLocal(new Date().toISOString(), timezone);
}

/**
 * The earliest pickup the operator's minimum-notice policy allows, as a
 * `datetime-local` value for the picker's `min` attribute (ONB-U9). A
 * convenience constraint only — the SERVER is the authoritative enforcer.
 * @param minHoursNotice - The minimum lead time in hours (0/undefined ⇒ "now").
 * @param timezone - Optional IANA zone (defaults to the browser's zone).
 * @returns The `YYYY-MM-DDTHH:mm` string for the earliest allowed pickup.
 */
export function datetimeLocalMinNotice(minHoursNotice?: number, timezone?: string): string {
  const hours = minHoursNotice && minHoursNotice > 0 ? minHoursNotice : 0;
  const at = new Date(Date.now() + hours * 60 * 60 * 1000);
  return isoToDatetimeLocal(at.toISOString(), timezone);
}
